import { useState, useEffect, useCallback } from 'react'
import { RequestService } from '../services/RequestService'
import { filterRows } from '../utils/filterRowsByTerm'

const useReportOptimizeCounterMeasure = () => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [resetRowsState, setResetRowsState] = useState([])
  const [filterOptions, setFilterOptions] = useState(null)
  const [selectedFilterOptions, setSelectedFilterOptions] = useState({})
  const [inputValue, setInputValue] = useState('')
  const [headersState, setHeadersState] = useState([])
  const [selectedHourRange, setSelectedHourRange] = useState(8)
  const [selectedOrder, setSelectedOrder] = useState(
    'working,prediction,high_fatigue,sleep',
  )
  const [graphMessage, setGraphMessage] = useState(null)
  const [loadingFilters, setLoadingFilters] = useState(true)
  const [selectedUser, setSelectedUser] = useState(null)

  const onSelectFilter = value => {
    setSelectedUser(null)
    setInputValue('')
    setSelectedFilterOptions(value)
  }

  const onSelectUser = value => {
    setSelectedUser(value)
    setInputValue(value.name)
    setSelectedFilterOptions([])
  }

  const isHourRangeSelected = ({ hourRange }) => {
    return selectedHourRange === hourRange
  }

  const createRows = ({ data }) => {
    const rows = data?.map(row => {
      return {
        id: row.id,
        employee: row.colaborator,
        shift: row.shift,
        position: row.position.title,
        level: row.position.level,
        team: row.team,
        fatigueLevel8h0: {
          fatigueLevel: row.data[0]?.predictionResult,
          isSleeping: row.data[0]?.isSleeping,
          isWorking: row.data[0]?.isWorking,
        },
        fatigueLevel8h1: {
          fatigueLevel: row.data[1]?.predictionResult,
          isSleeping: row.data[1]?.isSleeping,
          isWorking: row.data[1]?.isWorking,
        },
        fatigueLevel8h2: {
          fatigueLevel: row.data[2]?.predictionResult,
          isSleeping: row.data[2]?.isSleeping,
          isWorking: row.data[2]?.isWorking,
        },
        fatigueLevel8h3: {
          fatigueLevel: row.data[3]?.predictionResult,
          isSleeping: row.data[3]?.isSleeping,
          isWorking: row.data[3]?.isWorking,
        },
        fatigueLevel8h4: {
          fatigueLevel: row.data[4]?.predictionResult,
          isSleeping: row.data[4]?.isSleeping,
          isWorking: row.data[4]?.isWorking,
        },
        fatigueLevel8h5: {
          fatigueLevel: row.data[5]?.predictionResult,
          isSleeping: row.data[5]?.isSleeping,
          isWorking: row.data[5]?.isWorking,
        },
        fatigueLevel8h6: {
          fatigueLevel: row.data[6]?.predictionResult,
          isSleeping: row.data[6]?.isSleeping,
          isWorking: row.data[6]?.isWorking,
        },
        fatigueLevel8h7: {
          fatigueLevel: row.data[7]?.predictionResult,
          isSleeping: row.data[7]?.isSleeping,
          isWorking: row.data[7]?.isWorking,
        },
        fatigueLevel8h8: {
          fatigueLevel: row.data[0]?.optimizationResult,
          isSleeping: row.data[0]?.isSleeping,
          isWorking: row.data[0]?.isWorking,
        },
        fatigueLevel8h9: {
          fatigueLevel: row.data[1]?.optimizationResult,
          isSleeping: row.data[1]?.isSleeping,
          isWorking: row.data[1]?.isWorking,
        },
        fatigueLevel8h10: {
          fatigueLevel: row.data[2]?.optimizationResult,
          isSleeping: row.data[2]?.isSleeping,
          isWorking: row.data[2]?.isWorking,
        },
        fatigueLevel8h11: {
          fatigueLevel: row.data[3]?.optimizationResult,
          isSleeping: row.data[3]?.isSleeping,
          isWorking: row.data[3]?.isWorking,
        },
        fatigueLevel8h12: {
          fatigueLevel: row.data[4]?.optimizationResult,
          isSleeping: row.data[4]?.isSleeping,
          isWorking: row.data[4]?.isWorking,
        },
        fatigueLevel8h13: {
          fatigueLevel: row.data[5]?.optimizationResult,
          isSleeping: row.data[5]?.isSleeping,
          isWorking: row.data[5]?.isWorking,
        },
        fatigueLevel8h14: {
          fatigueLevel: row.data[6]?.optimizationResult,
          isSleeping: row.data[6]?.isSleeping,
          isWorking: row.data[6]?.isWorking,
        },
        fatigueLevel8h15: {
          fatigueLevel: row.data[7]?.optimizationResult,
          isSleeping: row.data[7]?.isSleeping,
          isWorking: row.data[7]?.isWorking,
        },
      }
    })

    return rows || []
  }

  const getPossibleFilters = async () => {
    if (!loadingFilters) setLoadingFilters(true)

    try {
      const { data } = await RequestService.getPredictionHistoryFilters()
      setFilterOptions(data)
    } catch (error) {
      console.error('Error on data fetch filters', error)
    }

    setLoadingFilters(false)
  }

  const onSelectOrderSave = ({ order }) => {
    const values = order.map(item => item.value)
    setSelectedOrder(values.join())
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await RequestService.getReportOptimizeCounterMeasure({
        groupBy: selectedHourRange,
        orderBy: selectedOrder,
        selectedFilterOptions,
        selectedUserId: selectedUser?.id,
      })

      const data = response?.data

      const rows = createRows({ data: data?.data })
      setResetRowsState(rows)
      setGraphMessage(data?.graphMessage)

      setRows(rows)
    } catch (e) {
      console.log('useReportOptimizeCounterMeasure', e)
    }

    setLoading(false)
  }, [selectedFilterOptions, selectedUser, selectedHourRange, selectedOrder])

  useEffect(() => {
    getPossibleFilters()
  }, [])

  useEffect(() => {
    fetchData()
  }, [selectedFilterOptions, selectedUser, selectedHourRange, selectedOrder])

  return {
    loading,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    onInputValueChange: setInputValue,
    setSelectedHourRange,
    selectedHourRange,
    isHourRangeSelected,
    fetchData,
    headersState,
    selectedFilterOptions,
    onSelectOrderSave,
    graphMessage,
    onSelectFilter,
    selectedUser,
    setSelectedUser,
    onSelectUser,
  }
}

export default useReportOptimizeCounterMeasure
